import { Alert, Box, Button, ButtonGroup, Divider, Heading, Modal, TextButton } from '@zillow/constellation-10';
import {
  useSatelliteWebLandlordLeadManagementHandler_getScreeningCriteria,
  useSatelliteWebLandlordLeadManagementHandler_setScreeningCriteria,
} from '@zg-rentals/ts-satellite-client-react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getErrorDisplayMessage } from '../../../utils/errorUtils';
import { buildComplianceFromCriteria } from './complianceFromCriteria';
import { buildCriteriaPayload, buildInitialFormState, type FormState } from './formState';
import FinancialExpectationsSection from './components/FinancialExpectationsSection';
import PetPolicySection from './components/PetPolicySection';
import RenterScreeningCriteriaModalSkeleton from './components/Skeleton';
import { formatCurrencyAmount } from '../../../utils/formatCurrencyAmount';
import { INCOME_TO_RENT_MULTIPLIER, NOT_APPLICABLE, PET_COUNT_MAX, PET_COUNT_MIN } from './constants';
import useBooleanConfig from '../../../hooks/useBooleanConfig';
import PropertySelector from './components/PropertySelector';
import StringConstants from '../../../constants/StringConstants';

type RenterScreeningCriteriaModalProps = {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  listingAlias?: string;
  onSaveSuccess?: () => void;
};

export default function RenterScreeningCriteriaModal({
  isOpen,
  onOpenChange,
  listingAlias = '',
  onSaveSuccess,
}: RenterScreeningCriteriaModalProps) {
  const [formState, setFormState] = useState<FormState | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const enableLeadManagementV2 = useBooleanConfig('enableLeadManagementV2', false);
  const [selectedListingAlias, setSelectedListingAlias] = useState(listingAlias);
  const saveErrorRef = useRef<HTMLDivElement>(null);

  const {
    data: criteriaData,
    isLoading: criteriaLoading,
    isError: criteriaError,
  } = useSatelliteWebLandlordLeadManagementHandler_getScreeningCriteria(
    { listingAlias: selectedListingAlias },
    {
      enabled: isOpen && !!selectedListingAlias,
      meta: {
        tags: ['satellite:propertyScreeningCriteria'],
        context: {
          api: 'getScreeningCriteria',
          details: 'RenterScreeningCriteriaModal - Error fetching criteria',
        },
      },
    },
  );

  const { price = 0, isEnabled = true } = criteriaData ?? {};

  const compliance = useMemo(() => buildComplianceFromCriteria(criteriaData?.criteria ?? []), [criteriaData?.criteria]);

  const hasInitializedForOpenSessionRef = useRef(false);

  const handlePropertyChange = useCallback((newListingAlias: string) => {
    setSelectedListingAlias(newListingAlias);
    setFormState(null);
    setSaveError(null);
    hasInitializedForOpenSessionRef.current = false;
  }, []);

  /** Sync form state when modal opens with criteria, and reset when modal closes.
      Only initialize once per open so refetches do not overwrite in-progress edits. */
  useEffect(() => {
    const criteriaList = criteriaData?.criteria ?? [];
    const criteriaPrice = criteriaData?.price ?? 0;
    if (isOpen && criteriaData != null && criteriaPrice >= 0) {
      if (!hasInitializedForOpenSessionRef.current) {
        setFormState(buildInitialFormState(criteriaList));
        hasInitializedForOpenSessionRef.current = true;
      }
    }
    if (!isOpen) {
      setFormState(null);
      setSaveError(null);
      hasInitializedForOpenSessionRef.current = false;
    }
  }, [isOpen, criteriaData]);

  // The modal body scrolls independently of the pinned footer, so a save can be triggered while
  // scrolled away from the alert at the bottom of the form. Bring it into view when it appears;
  // its scroll-margin keeps it clear of the footer rather than flush against it.
  useEffect(() => {
    if (saveError) {
      saveErrorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [saveError]);

  const { mutateAsync: setCriteria, isPending: isSaving } =
    useSatelliteWebLandlordLeadManagementHandler_setScreeningCriteria({
      meta: {
        invalidates: ['satellite:propertyScreeningCriteria'],
        context: {
          api: 'setScreeningCriteria',
          details: 'RenterScreeningCriteriaModal - Error saving criteria',
        },
      },
    });

  const handleCancel = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  const handleSave = useCallback(async () => {
    if (!formState || !selectedListingAlias) return;
    setSaveError(null);
    try {
      await setCriteria({
        postData: {
          listingAlias: selectedListingAlias,
          criteria: buildCriteriaPayload(formState, compliance),
        },
      });
      onSaveSuccess?.();
      onOpenChange(false);
    } catch (err) {
      // The Satellite endpoint rejects criteria that violate locale restrictions with a
      // human-readable validation message; surface that to the user instead of a generic failure.
      setSaveError(getErrorDisplayMessage(err, StringConstants.SCREENING_CRITERIA_SAVE_ERROR));
    }
  }, [formState, selectedListingAlias, setCriteria, compliance, onSaveSuccess, onOpenChange]);

  /** Update a single form field by key; keeps other fields unchanged. Type-safe: value must match the field's type. */
  const updateForm = useCallback(<K extends keyof FormState>(key: K, value: FormState[K]) => {
    setFormState((prev) => (prev ? { ...prev, [key]: value } : null));
  }, []);

  /** Minimum monthly income implied by selected income-to-rent ratio and listing price. Show "--" when not set (matches ZRM). */
  const minIncomeDisplay = useMemo(() => {
    if (!formState || price <= 0) return '--';
    const ratio = formState.incomeToRent;
    if (ratio === '' || ratio === NOT_APPLICABLE) return '--';
    const multiplier = INCOME_TO_RENT_MULTIPLIER[ratio] ?? 0;
    return multiplier === 0 ? '--' : formatCurrencyAmount(price * multiplier);
  }, [formState?.incomeToRent, price]);

  /** When pets allowed, at least one pet type must have max > 0 (matches ZRM). */
  const hasAtLeastOnePetType = useMemo(() => {
    if (!formState?.isPetsAllowed) return true;
    const cat = parseInt(formState.petCatCount, 10) || 0;
    const small = parseInt(formState.petSmallDogCount, 10) || 0;
    const large = parseInt(formState.petLargeDogCount, 10) || 0;
    return cat > 0 || small > 0 || large > 0;
  }, [formState?.isPetsAllowed, formState?.petCatCount, formState?.petSmallDogCount, formState?.petLargeDogCount]);

  const handlePetCountChange = useCallback(
    (key: 'petCatCount' | 'petSmallDogCount' | 'petLargeDogCount', delta: number) => {
      setFormState((prev) => {
        if (!prev) return null;
        const current = prev[key];
        const num = Math.min(PET_COUNT_MAX, Math.max(PET_COUNT_MIN, parseInt(current || '0', 10) + delta));
        return { ...prev, [key]: String(num) };
      });
    },
    [],
  );

  // The modal fetches its own criteria via getPropertyCriteria, independent of the lead-management
  // page. On a fetch error formState never initializes, so without this the modal would render the
  // skeleton indefinitely — surface the error instead.
  const renderCriteriaBody = () => {
    if (criteriaError) {
      return <Alert tone="critical">{StringConstants.SCREENING_CRITERIA_LOAD_ERROR}</Alert>;
    }
    if (criteriaLoading || !formState) {
      return <RenterScreeningCriteriaModalSkeleton />;
    }
    return (
      <>
        {!isEnabled && <Alert tone="info">{StringConstants.SCREENING_CRITERIA_NOT_ENABLED}</Alert>}
        <Box css={{ opacity: isEnabled ? 1 : 0.5, pointerEvents: isEnabled ? 'auto' : 'none' }}>
          <FinancialExpectationsSection
            formState={formState}
            price={price}
            minIncomeDisplay={minIncomeDisplay}
            updateForm={updateForm}
            compliance={compliance}
          />
          <Divider css={{ marginBlock: 'tighter' }} />
          <PetPolicySection formState={formState} updateForm={updateForm} onPetCountChange={handlePetCountChange} />
        </Box>
      </>
    );
  };

  return (
    <Modal
      open={isOpen}
      onOpenChange={onOpenChange}
      size={{ base: 'fullScreen', md: 'lg' }}
      header={
        <Heading level={2} textStyle="heading-xs">
          Renter screening criteria
        </Heading>
      }
      body={
        <Box css={{ display: 'flex', flexDirection: 'column', gap: 'default' }}>
          {enableLeadManagementV2 && (
            <PropertySelector listingAlias={selectedListingAlias} onPropertySelect={handlePropertyChange} />
          )}
          {renderCriteriaBody()}
          {saveError && (
            <Box ref={saveErrorRef} css={{ scrollMarginBottom: 'default' }}>
              <Alert tone="critical" onClose={() => setSaveError(null)}>
                {saveError}
              </Alert>
            </Box>
          )}
        </Box>
      }
      footer={
        <ButtonGroup aria-label="Screening criteria actions">
          <TextButton onClick={handleCancel}>Cancel</TextButton>
          <Button
            emphasis="filled"
            tone="brand"
            onClick={handleSave}
            // criteriaError guards the case where a refetch fails after an initial success: React Query
            // retains the prior data/formState, so without this Save would stay enabled over stale state
            // while the body shows only the error alert.
            disabled={!formState || criteriaLoading || criteriaError || isSaving || !hasAtLeastOnePetType || !isEnabled}
          >
            Save changes
          </Button>
        </ButtonGroup>
      }
    />
  );
}
